import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  EmailSubscriptionListItem,
  EmailSubscriptionQuery,
  EmailSubscriptionType,
} from '../../types/emailSubscription.ts';
import { PageResponse } from '../../types/subscription.ts';

interface SendEmailPayload {
  subscriptionType: EmailSubscriptionType | null;
  emails: string[] | null;
  subject: string;
  htmlContent: string;
}

interface EmailSubscriptionState {
  subscriptions?: PageResponse<EmailSubscriptionListItem>;
  /** Last query of the list, so it can be reloaded as-is after activating a subscription. */
  query: EmailSubscriptionQuery;
  isLoading: boolean;
  error: string | null;
  isSending: boolean;
  sendError: string | null;
  sendSuccess: boolean;
  isActivating: boolean;
  activateError: string | null;
  activateSuccess: boolean;
}

const initialState: EmailSubscriptionState = {
  subscriptions: undefined,
  query: {},
  isLoading: false,
  error: null,
  isSending: false,
  sendError: null,
  sendSuccess: false,
  isActivating: false,
  activateError: null,
  activateSuccess: false,
};

export const emailSubscriptionReducer = createSlice({
  name: 'emailSubscription',
  initialState,
  reducers: {
    loadEmailSubscriptions: (state: EmailSubscriptionState, action: PayloadAction<EmailSubscriptionQuery>) => {
      state.query = action.payload;
      state.isLoading = true;
      state.error = null;
    },
    setEmailSubscriptions: (
      state: EmailSubscriptionState,
      action: PayloadAction<PageResponse<EmailSubscriptionListItem>>,
    ) => {
      state.subscriptions = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    setEmailSubscriptionsError: (state: EmailSubscriptionState, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    sendEmail: (state: EmailSubscriptionState, _action: PayloadAction<SendEmailPayload>) => {
      state.isSending = true;
      state.sendError = null;
      state.sendSuccess = false;
    },
    setSendEmailSuccess: (state: EmailSubscriptionState) => {
      state.isSending = false;
      state.sendSuccess = true;
      state.sendError = null;
    },
    setSendEmailError: (state: EmailSubscriptionState, action: PayloadAction<string>) => {
      state.isSending = false;
      state.sendError = action.payload;
    },
    resetSendEmailStatus: (state: EmailSubscriptionState) => {
      state.sendError = null;
      state.sendSuccess = false;
    },
    activateEmailSubscription: (state: EmailSubscriptionState, _action: PayloadAction<number>) => {
      state.isActivating = true;
      state.activateError = null;
      state.activateSuccess = false;
    },
    setActivateEmailSubscriptionSuccess: (state: EmailSubscriptionState) => {
      state.isActivating = false;
      state.activateSuccess = true;
      state.activateError = null;
    },
    setActivateEmailSubscriptionError: (state: EmailSubscriptionState, action: PayloadAction<string>) => {
      state.isActivating = false;
      state.activateError = action.payload;
    },
    resetActivateStatus: (state: EmailSubscriptionState) => {
      state.activateError = null;
      state.activateSuccess = false;
    },
  },
});
